import React from 'react';
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { ManagedGraphsSection } from '@/modules/shared/DataLayers/components';

export interface DataLayerConfig {
  id: string;
  condition?: () => boolean;
  component: () => React.ReactNode;
}

/**
 * Waveform configuration from metadata
 * This structure is defined in backend metadata and injected in dataLayersApi.ts
 */
export interface WaveformConfig {
  previewDuration?: number;      // seconds - initial preview (default: 5)
  fullDuration?: number;         // seconds - full waveform on demand (default: 30)
  sampleRate?: number;           // Hz (default: 4000)
  audioEnabled?: boolean;
  audioSampleRate?: number;      // Hz - for audio playback (default: 44100)
  recordingTypes?: Array<{
    id: string;
    label: string;
    unit: string;
    enabled: boolean;
    isDefault: boolean;
  }>;
  features?: {
    enableMarkers?: boolean;
    enableHarmonics?: boolean;
    enableSideBands?: boolean;
    enableDrawing?: boolean;
    enableCompareTable?: boolean;
    enableRegions?: boolean;
    enableZoom?: boolean;
    enableLoop?: boolean;
    showFloatingToolbar?: boolean;
    showYAxisScale?: boolean;
    enableDynamicMargin?: boolean;
  };
  zoom?: {
    min?: number;
    max?: number;
    step?: number;
    default?: number;
  };
  autoscale?: {
    mode?: 'none' | 'onload' | 'onDataLoad';
    axis?: 'x' | 'y' | 'both';
  };
}

type LegendSyncReturn = {
  mode: 'multi' | 'focus';
  toggleMode: () => void;
  getSavedState?: () => Record<string, Record<string, boolean>> | null;
  clearSavedState?: () => void;
};

export interface CreateWaveformDataLayersConfigProps {
  activeDataLayers: string[];
  baseTitle?: string;
  from: string;
  to: string;
  waveformGraphs: any[];
  waveformManager: any;
  enhancedQueryParams: any;
  forceDataRefresh: number;
  handleSensorLoadingStateChange: (isLoading: boolean) => void;
  selectedComponentId?: string | null;
  selectedComponentObj?: any;
  legendSync?: LegendSyncReturn;
  onToggleSensor?: (sectionId: string, sensorName: string) => void;
  registerSensorData?: (
    dataLayer: string,
    enabledSensors: Record<string, boolean>,
    sensorColors: Record<string, string>
  ) => void;
  allEnabledSensors?: Record<string, Record<string, boolean>>;
  allSensorColors?: Record<string, Record<string, string>>;
  masterSensorColorMap?: Record<string, string>;
  selectedMarkerTimestamp?: string | null;
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void;
  selectedTimestampsSet?: Set<string>;
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  debouncedSelectedTimestampsSet?: Set<string>;
  verticalLinesState?: any;
  sensorFusionVerticalLines?: any;
  onRemoveSensorFusionMarker?: (markerId: string) => void;
  onClearAllSensorFusionMarkers?: () => void;
  focusedSensors?: Record<string, string>;
  onFocus?: (sensorName: string, sectionId?: string, currentEnabledSensors?: Record<string, Record<string, boolean>>) => void;
  focusMode?: boolean;
  setFocusMode?: (focusMode: boolean) => void;
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
  setTriggerSensorReset: (value: boolean) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onRemoveMarker?: (markerId: string) => void;
  config?: any;
  allowMultiTimestampMode?: boolean;
  expandableLegendSeries?: any[];
  popoverTimestamps?: any[];
  enabledSeries?: Set<string>;
}

/**
 * Creates the data layers configuration for the Waveform tab
 * Uses ManagedGraphsSection with WaveSurfer.js chart for audio visualization
 *
 * Configuration is read from metadata (config prop) with fallback defaults
 */
export const createWaveformDataLayersConfig = (props: CreateWaveformDataLayersConfigProps): DataLayerConfig[] => {
  const {
    activeDataLayers,
    baseTitle,
    waveformGraphs,
    waveformManager,
    enhancedQueryParams,
    forceDataRefresh,
    handleSensorLoadingStateChange,
    legendSync,
    onToggleSensor,
    registerSensorData,
    allEnabledSensors,
    allSensorColors,
    masterSensorColorMap,
    selectedMarkerTimestamp,
    setSelectedMarkerTimestamp,
    selectedTimestampsSet,
    verticalLinesState,
    sensorFusionVerticalLines,
    onRemoveSensorFusionMarker,
    onClearAllSensorFusionMarkers,
    focusedSensors,
    onFocus,
    focusMode,
    setFocusMode,
    loadingSensors,
    setLoadingSensor,
    clearLoadingSensor,
    setTriggerSensorReset,
    onToggleExpandableTimestamp,
    onToggleExpandableSeries,
    onToggleExpandableSeriesFocus,
    onRemoveMarker,
    allowMultiTimestampMode,
    expandableLegendSeries,
    popoverTimestamps,
    enabledSeries,
    config,
  } = props;

  // Extract waveform-specific configuration from metadata
  const waveformDataLayer = config?.dataLayers?.find((layer: any) => layer.id === 'waveform');
  const waveformConfig = waveformDataLayer?.waveformConfig || {};
  const expandableLegendConfig = waveformDataLayer?.expandableLegend || {};
  const features = waveformConfig.features || {};
  const autoscaleConfig = waveformConfig.autoscale || {};

  // Build title from metadata or use default
  const dataLayerTitle = waveformDataLayer?.title || 'Time Waveform';
  const title = `${baseTitle || ''} ${dataLayerTitle}`.trim();

  // Extract feature flags with defaults (backward compatible)
  const enableMarkers = features.enableMarkers ?? true;
  const enableHarmonics = features.enableHarmonics ?? false;
  const enableSideBands = features.enableSideBands ?? false;
  const enableDrawing = features.enableDrawing ?? false;
  const enableCompareTable = features.enableCompareTable ?? false;
  const showFloatingToolbar = features.showFloatingToolbar ?? true;
  const showYAxisScale = features.showYAxisScale ?? true;
  const enableDynamicMargin = features.enableDynamicMargin ?? true;

  // Extract autoscale settings with defaults
  const autoscaleMode = autoscaleConfig.mode || 'onDataLoad';
  const autoscaleAxis = autoscaleConfig.axis || 'both';

  // Extract expandable legend label from config
  const expandableLegendLabel = expandableLegendConfig.label || 'Timestamps';

  return [
    {
      id: SENSOR_FUSION_DATA_LAYERS.WAVEFORM,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.WAVEFORM),
      component: () => (
        <ManagedGraphsSection
          title={title}
          dataLayer={SENSOR_FUSION_DATA_LAYERS.WAVEFORM}
          graphs={waveformGraphs}
          selectedSections={waveformManager.selectedSections}
          onSelectionChange={waveformManager.handleSelectionChange}
          showReset={true}
          syncSectionLegends={true}
          syncMarking={true}
          hideManagedGraphsDropdown={true}
          enableCompareTable={enableCompareTable}
          enableMarkers={enableMarkers}
          enableHarmonics={enableHarmonics}
          enableSideBands={enableSideBands}
          enableDrawing={enableDrawing}
          expandableLegendLabel={expandableLegendLabel}
          showFloatingToolbar={showFloatingToolbar}
          autoscaleMode={autoscaleMode}
          autoscaleAxis={autoscaleAxis}
          showYAxisScale={showYAxisScale}
          enableDynamicMargin={enableDynamicMargin}
          triggerReset={waveformManager.triggerReset}
          onReset={() => {
            setTriggerSensorReset(true);
            waveformManager.handleReset();
            if (onClearAllSensorFusionMarkers) {
              onClearAllSensorFusionMarkers();
            }
            if (setSelectedMarkerTimestamp) {
              setSelectedMarkerTimestamp(null);
            }
          }}
          onResetDataLayer={() => {
            waveformManager.handleDataLayerReset();
            setTriggerSensorReset(false);
          }}
          loadingState={waveformManager.loadingState}
          queryParams={{
            ...enhancedQueryParams,
            data_layer: SENSOR_FUSION_DATA_LAYERS.WAVEFORM,
          }}
          forceRefresh={forceDataRefresh}
          onLoadingStateChange={(isLoading) => {
            waveformManager.handleLoadingStateChange(isLoading);
            handleSensorLoadingStateChange(isLoading);
          }}
          includeAlarms={false}
          variant="lines"
          verticalLinesState={verticalLinesState}
          selectedTimestampsSet={selectedTimestampsSet}
          onToggleSensor={onToggleSensor}
          registerSensorData={registerSensorData}
          sectionEnabledSensors={{ sensors: allEnabledSensors?.[SENSOR_FUSION_DATA_LAYERS.WAVEFORM] || {} }}
          sectionSensorColors={{ sensors: allSensorColors?.[SENSOR_FUSION_DATA_LAYERS.WAVEFORM] || {} }}
          masterSensorColorMap={masterSensorColorMap}
          isSensorLoading={(_sectionId, sensorName) => {
            return loadingSensors?.has(sensorName) || false;
          }}
          showInlineFocusButton={true}
          legendSyncMode={legendSync?.mode}
          focusedSensors={focusedSensors}
          onFocus={onFocus}
          onToggleFocusMode={legendSync?.toggleMode}
          loadingSensors={loadingSensors}
          setLoadingSensor={setLoadingSensor}
          clearLoadingSensor={clearLoadingSensor}
          selectedMarkerTimestamp={selectedMarkerTimestamp}
          expandableLegendFocusMode={focusMode && allowMultiTimestampMode}
          expandableLegendSeries={expandableLegendSeries || []}
          popoverTimestamps={popoverTimestamps}
          enabledSeries={enabledSeries}
          onToggleExpandableSeries={onToggleExpandableSeries}
          onToggleExpandableSeriesFocus={onToggleExpandableSeriesFocus}
          onToggleExpandableTimestamp={onToggleExpandableTimestamp}
          onRemoveMarker={onRemoveMarker}
        />
      ),
    },
  ];
};

/**
 * Render order for waveform data layers
 */
export const WAVEFORM_DATA_LAYERS_RENDER_ORDER = [
  SENSOR_FUSION_DATA_LAYERS.WAVEFORM,
];
